import React, { useState, useEffect, useCallback } from 'react';
import Modal from '@mui/material/Modal';
import Backdrop from '@mui/material/Backdrop';
import Fade from '@mui/material/Fade';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { t } from 'i18next';

const FIELD_TYPES = [
  'text', 'long_text', 'number', 'date', 'email', 'phone', 'url',
  'select', 'multi_select', 'checkbox', 'currency', 'file', 'image',
  'date_range', 'address', 'user',
];

const SECTIONS = [
  { value: 'hr_user',          label: 'HR / Users' },
  { value: 'crm_contact',      label: 'CRM Contact' },
  { value: 'crm_organisation', label: 'CRM Organisation' },
  { value: 'equipment',        label: 'Equipment' },
  { value: 'expenses',         label: 'Expenses' },
  { value: 'finance_car',      label: 'Company Vehicles' },
  { value: 'insurance',        label: 'Insurance' },
];

interface ContactType { id: number; name: string; }
interface ReplaceItem { option: string; count: number; replacement: string | null; }

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  editField?: any;
  defaultSection?: string;
}

export default function AddCustomField({ open, onClose, onSaved, editField, defaultSection }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [columnName, setColumnName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('text');
  const [tableName, setTableName] = useState('hr_user');
  const [isRequired, setIsRequired] = useState(false);
  const [isShown, setIsShown] = useState(true);
  const [options, setOptions] = useState<string[]>([]);
  const [optionInput, setOptionInput] = useState('');

  // Scope (contact types, only for crm_contact)
  const [contactTypes, setContactTypes] = useState<ContactType[]>([]);
  const [selectedTypeIds, setSelectedTypeIds] = useState<Set<number>>(new Set());

  // Replace step
  const [step, setStep] = useState<'form' | 'replace'>('form');
  const [replaceItems, setReplaceItems] = useState<ReplaceItem[]>([]);
  const [saving, setSaving] = useState(false);

  const loadContactTypes = useCallback(async () => {
    try {
      const { data } = await axios.get('crm/contact-types');
      setContactTypes(data);
    } catch {}
  }, []);

  useEffect(() => { loadContactTypes(); }, [loadContactTypes]);

  // Initialise form when editField changes or modal opens
  useEffect(() => {
    if (editField) {
      setColumnName(editField.column_name);
      setDescription(editField.description ?? '');
      setType(editField.type);
      setTableName(editField.table_name);
      setIsRequired(editField.isRequired);
      setIsShown(editField.isShown);
      setOptions(editField.options ?? []);
    } else {
      setColumnName('');
      setDescription('');
      setType('text');
      setTableName(defaultSection ?? 'hr_user');
      setIsRequired(false);
      setIsShown(true);
      setOptions([]);
    }
    setOptionInput('');
    setStep('form');
    setReplaceItems([]);
  }, [editField, open]);

  // Sync selectedTypeIds when contactTypes load or editField changes
  useEffect(() => {
    if (contactTypes.length === 0) return;
    if (editField?.typeIds?.length > 0) {
      setSelectedTypeIds(new Set(editField.typeIds));
    } else {
      // All active by default
      setSelectedTypeIds(new Set(contactTypes.map((ct) => ct.id)));
    }
  }, [editField, contactTypes, open]);

  const needsOptions = type === 'select' || type === 'multi_select';

  // Add options — supports comma-separated input
  const addOptions = () => {
    const parts = optionInput.split(',').map((s) => s.trim()).filter(Boolean);
    if (parts.length === 0) return;
    setOptions((prev) => {
      const next = [...prev];
      parts.forEach((p) => { if (!next.includes(p)) next.push(p); });
      return next;
    });
    setOptionInput('');
  };

  const removeOption = (opt: string) => setOptions((prev) => prev.filter((o) => o !== opt));

  const toggleType = (id: number) => {
    setSelectedTypeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const buildPayload = () => {
    // If all contact types selected (or not a crm_contact field) → null (means "all")
    const allSelected = tableName === 'crm_contact'
      ? selectedTypeIds.size === contactTypes.length
      : true;
    return {
      table_name: tableName,
      column_name: columnName.trim().toLowerCase().replace(/\s+/g, '_'),
      description: description.trim() || null,
      type,
      isRequired,
      isShown,
      ...(needsOptions ? { options } : {}),
      typeIds: tableName === 'crm_contact' && !allSelected
        ? Array.from(selectedTypeIds)
        : null,
    };
  };

  const persistField = async () => {
    try {
      if (editField?.id) {
        await axios.put(`fields/${editField.id}`, buildPayload());
        enqueueSnackbar('Field updated', { variant: 'success' });
      } else {
        await axios.post('fields/add', buildPayload());
        enqueueSnackbar('Field created', { variant: 'success' });
      }
      onSaved();
      onClose();
    } catch {
      enqueueSnackbar('Error saving field', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!columnName.trim()) {
      enqueueSnackbar(t('Required'), { variant: 'error' });
      return;
    }

    // Check for removed options only when editing an existing select/multi_select field
    if (editField?.id && needsOptions) {
      const originalOptions: string[] = editField.options ?? [];
      const removed = originalOptions.filter((o) => !options.includes(o));

      if (removed.length > 0) {
        setSaving(true);
        try {
          const params = new URLSearchParams();
          removed.forEach((o) => params.append('options', o));
          const { data } = await axios.get(`fields/${editField.id}/option-usage?${params}`);
          const affected: ReplaceItem[] = removed
            .filter((o) => (data[o] ?? 0) > 0)
            .map((o) => ({ option: o, count: data[o], replacement: options[0] ?? null }));

          if (affected.length > 0) {
            setReplaceItems(affected);
            setStep('replace');
            setSaving(false);
            return;
          }
        } catch {
          // Endpoint unavailable — skip check and save anyway
        }
        setSaving(false);
      }
    }

    setSaving(true);
    await persistField();
  };

  const handleConfirmReplace = async () => {
    setSaving(true);
    try {
      for (const item of replaceItems) {
        await axios.post(`fields/${editField.id}/replace-option`, {
          oldValue: item.option,
          newValue: item.replacement,
        });
      }
      await persistField();
    } catch {
      enqueueSnackbar('Error replacing values', { variant: 'error' });
      setSaving(false);
    }
  };

  const setReplacement = (option: string, val: string | null) => {
    setReplaceItems((prev) =>
      prev.map((r) => r.option === option ? { ...r, replacement: val } : r)
    );
  };

  // ─── Shared pill style ───
  const pill = (active: boolean, color?: string) =>
    `flex items-center gap-[4px] text-[12px] px-[9px] py-[3px] rounded-full border cursor-pointer select-none transition-all ${
      active
        ? 'border-transparent text-white'
        : 'bg-transparent border-[var(--border-color)] text-[var(--text-color-calendar-week)]'
    }`;

  return (
    <Modal open={open} onClose={onClose}
      closeAfterTransition disableScrollLock
      slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 500 } }}>
      <Fade in={open}>
        <Box onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500, bgcolor: 'var(--bg-form)',
          border: 'var(--border-input)', borderRadius: '8px',
          boxShadow: 24, p: 3, maxHeight: '90vh', overflowY: 'auto',
        }}>

          {/* ── Field form ── */}
          {step === 'form' && (
            <>
              <div className="text-[16px] font-semibold mb-[16px] text-[var(--text-color)]">
                {editField ? t('Edit') : t('AddField')}
              </div>

              {/* Section */}
              <div className="flex flex-col mb-[12px]">
                <label className="text-[10px] uppercase tracking-wider font-semibold ml-[2px] mb-[4px] text-[var(--text-color-calendar-week)]">
                  {t('Section')}
                </label>
                <select className="edit-select p-[6px] rounded-[5px] edit-input text-[13px]"
                  value={tableName} onChange={(e) => setTableName(e.target.value)}>
                  {SECTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>

              {/* Name */}
              <div className="flex flex-col mb-[12px]">
                <label className="text-[10px] uppercase tracking-wider font-semibold ml-[2px] mb-[4px] text-[var(--text-color-calendar-week)]">
                  {t('Name')}
                </label>
                <input
                  className="w-full text-[13px] py-[6px] px-[8px] rounded-[5px] bg-[var(--bg-input)] text-[var(--text-color)] timesheet-edit-input"
                  type="text"
                  placeholder="e.g. emergency_contact"
                  value={columnName}
                  onChange={(e) => setColumnName(e.target.value)}
                />
              </div>

              {/* Description */}
              <div className="flex flex-col mb-[12px]">
                <label className="text-[10px] uppercase tracking-wider font-semibold ml-[2px] mb-[4px] text-[var(--text-color-calendar-week)]">
                  Description
                </label>
                <textarea
                  className="w-full text-[13px] py-[6px] px-[8px] rounded-[5px] bg-[var(--bg-input)] text-[var(--text-color)] timesheet-edit-input"
                  style={{ resize: 'vertical', minHeight: '64px', lineHeight: 1.5, fontFamily: 'inherit' }}
                  placeholder="Explain when and why this field is used…"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              {/* Type */}
              <div className="flex flex-col mb-[12px]">
                <label className="text-[10px] uppercase tracking-wider font-semibold ml-[2px] mb-[4px] text-[var(--text-color-calendar-week)]">
                  {t('FieldType')}
                </label>
                <select className="edit-select p-[6px] rounded-[5px] edit-input text-[13px]"
                  value={type} onChange={(e) => setType(e.target.value)}>
                  {FIELD_TYPES.map((ft) => <option key={ft} value={ft}>{ft}</option>)}
                </select>
              </div>

              {/* Options (select / multi_select only) */}
              {needsOptions && (
                <div className="mb-[12px]">
                  <label className="text-[10px] uppercase tracking-wider font-semibold ml-[2px] mb-[4px] text-[var(--text-color-calendar-week)] block">
                    Options
                  </label>
                  {/* Input + Add button */}
                  <div className="flex gap-[6px] mb-[8px]">
                    <input
                      className="flex-1 text-[13px] py-[6px] px-[8px] rounded-[5px] bg-[var(--bg-input)] text-[var(--text-color)] timesheet-edit-input"
                      type="text"
                      placeholder="Option name (or several separated by comma)"
                      value={optionInput}
                      onChange={(e) => setOptionInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addOptions()}
                    />
                    <button
                      type="button"
                      onClick={addOptions}
                      className="btn-primary">
                      Add
                    </button>
                  </div>
                  {/* Pills */}
                  {options.length > 0 && (
                    <div className="flex flex-wrap gap-[6px]">
                      {options.map((opt) => (
                        <span key={opt}
                          className="inline-flex items-center gap-[5px] text-[12px] px-[10px] py-[3px] rounded-full bg-[var(--bg-form-task)] border border-[var(--border-color)] text-[var(--text-color)]">
                          {opt}
                          <button
                            type="button"
                            title={`Remove "${opt}"`}
                            onClick={() => removeOption(opt)}
                            className="flex items-center justify-center w-[14px] h-[14px] rounded-full hover:bg-black/10 transition-colors text-[var(--text-color-calendar-week)] hover:text-[var(--text-color)] leading-none">
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Scope (contact types, crm_contact only) */}
              {tableName === 'crm_contact' && contactTypes.length > 0 && (
                <div className="mb-[12px]">
                  <label className="text-[10px] uppercase tracking-wider font-semibold ml-[2px] mb-[4px] text-[var(--text-color-calendar-week)] block">
                    Scope — Contact Types
                  </label>
                  <div className="text-[11px] text-[var(--text-color-calendar-week)] mb-[8px]">
                    Field will be shown only for selected types. All active = shown for everyone.
                  </div>
                  <div className="flex flex-wrap gap-[6px]">
                    {contactTypes.map((ct) => {
                      const active = selectedTypeIds.has(ct.id);
                      return (
                        <button
                          key={ct.id}
                          type="button"
                          title={ct.name}
                          onClick={() => toggleType(ct.id)}
                          className="inline-flex items-center gap-[5px] text-[12px] px-[10px] py-[3px] rounded-full border transition-all select-none"
                          style={{
                            backgroundColor: active ? '#6366f1' : 'transparent',
                            borderColor: active ? '#6366f1' : 'var(--border-color)',
                            color: active ? '#fff' : 'var(--text-color-calendar-week)',
                          }}>
                          {ct.name}
                        </button>
                      );
                    })}
                  </div>
                  {selectedTypeIds.size === 0 && (
                    <div className="text-[11px] text-orange-400 mt-[6px]">
                      ⚠ No types selected — field will be hidden everywhere
                    </div>
                  )}
                </div>
              )}

              {/* Flags */}
              <div className="flex gap-[20px] mb-[18px] text-[var(--text-color)]">
                <label className={`flex items-center gap-[6px] text-[13px] ${isShown ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'}`}>
                  <input type="checkbox" disabled={!isShown} checked={isRequired}
                    onChange={(e) => setIsRequired(e.target.checked)} />
                  {t('Required')}
                </label>
                <label className="flex items-center gap-[6px] text-[13px] cursor-pointer">
                  <input type="checkbox" checked={isShown} onChange={(e) => {
                    const v = e.target.checked;
                    setIsShown(v);
                    if (!v) setIsRequired(false);
                  }} />
                  {t('IsActive')}
                </label>
              </div>

              <div className="flex gap-[10px]">
                <Button variant="contained" sx={{ flex: 1, fontSize: 13 }} disabled={saving} onClick={handleSave}>
                  {saving ? '...' : editField ? t('Change') : t('Add')}
                </Button>
                <Button variant="outlined" sx={{ flex: 1, fontSize: 13 }} onClick={onClose}>
                  {t('Cancel')}
                </Button>
              </div>
            </>
          )}

          {/* ── Replace step ── */}
          {step === 'replace' && (
            <>
              <div className="text-[15px] font-semibold mb-[4px] text-[var(--text-color)]">
                Removed options are in use
              </div>
              <div className="text-[12px] text-[var(--text-color-calendar-week)] mb-[16px]">
                The options below are currently assigned to records. Choose a replacement for each,
                or select "Clear" to remove the value from those records.
              </div>

              <div className="flex flex-col gap-[10px] mb-[20px]">
                {replaceItems.map((item) => (
                  <div key={item.option}
                    className="rounded-[6px] border border-[var(--border-input)] bg-[var(--btn-user-form)] p-[12px]">
                    <div className="flex items-center justify-between mb-[8px]">
                      <span className="inline-flex items-center gap-[5px] text-[12px] px-[9px] py-[2px] rounded-full bg-[var(--bg-form-task)] border border-[var(--border-color)] text-[var(--text-color)]">
                        {item.option}
                      </span>
                      <span className="text-[11px] text-[var(--text-color-calendar-week)]">
                        {item.count} record{item.count !== 1 ? 's' : ''} affected
                      </span>
                    </div>
                    <div>
                      <label className="text-[10px] text-[var(--text-color-calendar-week)] mb-[4px] block uppercase tracking-wider">
                        Replace with
                      </label>
                      <select
                        className="edit-select p-[5px] rounded-[5px] edit-input text-[13px] w-full"
                        value={item.replacement ?? '__clear__'}
                        onChange={(e) =>
                          setReplacement(item.option, e.target.value === '__clear__' ? null : e.target.value)
                        }>
                        <option value="__clear__">— Clear value —</option>
                        {options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-[10px]">
                <Button variant="contained" sx={{ flex: 1, fontSize: 13 }} disabled={saving}
                  onClick={handleConfirmReplace}>
                  {saving ? 'Saving...' : 'Apply & Save'}
                </Button>
                <Button variant="outlined" sx={{ flex: 1, fontSize: 13 }}
                  onClick={() => setStep('form')}>
                  Back
                </Button>
              </div>
            </>
          )}
        </Box>
      </Fade>
    </Modal>
  );
}
